/* eslint-disable no-magic-numbers */
import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { FormattedMessage, useIntl } from 'react-intl';

// TODO: Replace with Sage Carbon Autocomplete when MultiSelect is updated
import { Autocomplete, Chip, TextField } from '@mui/material';

import Tooltip from 'carbon-react/lib/components/tooltip';
import Icon from 'carbon-react/lib/components/icon';

import { ForecastListItem } from '@futrli-sdk/predict';
import { ConsolidatedForecastListItem } from '@futrli-sdk/consolidations';

import { hasNoAccess } from '../../../available-forecasts';
import { useReportBuilderContext } from '../../../report-builder.context';
import { ConfirmModal } from '../../modals/confirm-modal';
import { useI18nConstants } from '../../../use-i18n-constants';

import styles from './builder-panel.scss';

export const DatasetsSelector = () => {
  const intl = useIntl();
  const {
    forecasts,
    getSelectedDatasets,
    setSelectedDatasets,
    isTemplate,
    isWorkspaceTemplate,
    dataReferenceMapping,
    setDatasetsToMap,
    setDataRefsToMap,
    setMapTemplateDataModalOpen,
    setMapDatasetsModalOpen,
    reportDsToMap,
    getDatasetName
  } = useReportBuilderContext();
  const { actualForecasts, dummyTemplateForecasts } = useI18nConstants();
  const isFutrliTemplate = isTemplate && !isWorkspaceTemplate;
  const selectedDatasets = getSelectedDatasets();

  const filteredDatasets = useMemo(() => {
    return isFutrliTemplate
      ? selectedDatasets.filter(
          sd => !!(actualForecasts.find(af => af.id === sd) || dummyTemplateForecasts.find(f => f.id === sd))
        )
      : selectedDatasets.filter(
          sd =>
            !!(
              actualForecasts.find(af => af.id === sd) ||
              forecasts.find(f => f.id === sd) ||
              dummyTemplateForecasts.find(f => f.id === sd) ||
              dataReferenceMapping?.dataset_references?.[sd]
            )
        );
  }, [
    isFutrliTemplate,
    selectedDatasets,
    actualForecasts,
    dummyTemplateForecasts,
    forecasts,
    dataReferenceMapping?.dataset_references
  ]);
  const [selectedFs, setSelectedFs] = useState<(ForecastListItem | ConsolidatedForecastListItem)[]>([]);
  const [noAccessDatasetToRemove, setNoAccessDatasetToRemove] = useState<string | null>(null);

  useEffect(() => {
    const selectedFs: (ForecastListItem | ConsolidatedForecastListItem)[] = [];
    filteredDatasets.forEach(d => {
      const foundDS = actualForecasts.find(af => af.id === d) || forecasts.find(f => f.id === d);
      if (foundDS) {
        selectedFs.push(foundDS);
        return;
      }
      const foundDF = dummyTemplateForecasts.find(dtf => dtf.id === d);
      if (foundDF) {
        selectedFs.push(foundDF);
        return;
      }
      const foundDR = reportDsToMap.find(ds => ds.id === d);
      if (foundDR) {
        selectedFs.push({ id: d, name: foundDR.name } as ForecastListItem);
        return;
      }
    });
    setSelectedFs(selectedFs);
  }, [actualForecasts, dummyTemplateForecasts, filteredDatasets, forecasts, reportDsToMap]);

  const showDSMapping = (d: string) => {
    if (dataReferenceMapping?.dataset_references?.[d]) {
      setDatasetsToMap([d]);
      setDataRefsToMap([]);
      setMapTemplateDataModalOpen(true);
    } else {
      setDatasetsToMap(filteredDatasets.filter(sd => dummyTemplateForecasts.find(f => f.id === sd)));
      setMapDatasetsModalOpen(true);
    }
  };

  const isNoAccessDataset = (id: string) => {
    const forecast = forecasts.find(f => f.id === id);
    return !!forecast && hasNoAccess(forecast);
  };

  const onSelectedDatasetsChange = (newValue: (ForecastListItem | ConsolidatedForecastListItem)[]) => {
    // datasets the user has no access to can't be added back once removed, so we ask for confirmation first
    const removedNoAccessDataset = selectedFs.find(
      ds => isNoAccessDataset(ds.id) && !newValue.some(v => v.id === ds.id)
    );
    if (removedNoAccessDataset) {
      setNoAccessDatasetToRemove(removedNoAccessDataset.id);
      return;
    }
    setSelectedDatasets(newValue.map(ds => ds.id));
  };

  const removeDataset = (value: string) => {
    setSelectedDatasets(selectedFs.filter(ds => ds.id !== value).map(ds => ds.id));
  };

  const onUnselect = (value: string) => {
    if (isNoAccessDataset(value)) {
      setNoAccessDatasetToRemove(value);
      return;
    }
    removeDataset(value);
  };

  const onConfirmRemoveNoAccessDataset = () => {
    if (noAccessDatasetToRemove) {
      removeDataset(noAccessDatasetToRemove);
    }
    setNoAccessDatasetToRemove(null);
  };

  // const renderMenuItem = (f: ForecastListItem | ConsolidatedForecastListItem, isDisabled: boolean) => (
  //   <MenuItem
  //     key={f.id}
  //     value={f.id}
  //     style={{
  //       fontSize: 14,
  //       opacity: isDisabled ? 0.3 : 1,
  //       cursor: isDisabled ? 'not-allowed' : 'default'
  //     }}
  //   >
  //     {f.name}
  //   </MenuItem>
  // );

  // const renderDSName = (f: ForecastListItem | ConsolidatedForecastListItem) => {
  //   const isDisabled = disableDatasets && !selectedFs.find(d => d.id === f.id);
  //   return isDisabled ? (
  //     <Tooltip
  //       message={intl.formatMessage({
  //         id: 'reporting.remove_selected_columns_to_add_more',
  //         defaultMessage: 'Remove selected "Column(s)" to add more'
  //       })}
  //     >
  //       {renderMenuItem(f, isDisabled)}
  //     </Tooltip>
  //   ) : (
  //     renderMenuItem(f, isDisabled)
  //   );
  // };

  // Datasets the user has no access to are only listed when a widget already uses them (they can't be selected again)
  const forecastOptions = useMemo(
    () => [...actualForecasts, ...forecasts.filter(f => !hasNoAccess(f) || selectedDatasets.includes(f.id))],
    [forecasts, actualForecasts, selectedDatasets]
  );

  const shouldGroup = forecasts.length > 0;

  return (
    <>
      <h3>
        <FormattedMessage id="reporting.datasets" defaultMessage="Datasets" />
        <Icon
          type="error"
          fontSize="small"
          tooltipMessage={
            <FormattedMessage
              id="reporting.datasets_header.information"
              defaultMessage="All published forecasts will be available"
            />
          }
          tooltipPosition="bottom"
          tooltipBgColor="#5d6579"
          ml="4px"
        />
      </h3>

      {/* This will be replaced with Sage Carbon autocomplete in the future where this problem is not happening
          that's why aria-hidden is added */}
      <div aria-hidden="true">
        <label htmlFor="datasets-multi-select" className={styles.visuallyHidden}>
          <FormattedMessage id="reporting.datasets_multi_select" defaultMessage="Datasets multi select" />
        </label>
        <Autocomplete
          id="datasets-multi-select"
          className={clsx(styles.dropdownSelector)}
          multiple
          disablePortal
          disableCloseOnSelect
          selectOnFocus
          openOnFocus
          disableClearable
          includeInputInList
          autoHighlight
          value={selectedFs}
          groupBy={
            shouldGroup
              ? option =>
                  actualForecasts.find(o => o.id === option.id)
                    ? intl.formatMessage({ id: 'reporting.actuals', defaultMessage: 'Actuals' })
                    : intl.formatMessage({ id: 'reporting.forecasts', defaultMessage: 'Forecasts' })
              : undefined
          }
          onChange={(_, v) => onSelectedDatasetsChange(v)}
          options={forecastOptions}
          getOptionLabel={option => option.name}
          // renderOption={renderDSName}
          renderInput={params => (
            <TextField
              {...params}
              id="datasets-multi-select"
              inputProps={{
                ...params.inputProps,
                'aria-hidden': 'true',
                'tabIndex': -1
              }}
            />
          )}
          renderTags={(selected, _) => (
            <div className={clsx(styles.chips)}>
              {selected.map((ds, i) => {
                const value = ds.id;
                const isTemplateDSinReport =
                  (!isFutrliTemplate && dummyTemplateForecasts.find(f => f.id === value)) ||
                  reportDsToMap.find(ds => ds.id === value);

                return isTemplateDSinReport ? (
                  <Tooltip
                    key={`${value}-${i}`}
                    message={intl.formatMessage(
                      {
                        id: 'reporting.map_value_or_delete_from_widget',
                        defaultMessage: 'Map {target}, or delete from widget'
                      },
                      { target: getDatasetName(value) }
                    )}
                  >
                    <Chip
                      label={
                        <span className={styles.clickToMap}>
                          {getDatasetName(value)}{' '}
                          <span className={styles.editIconWrapper} onClick={() => showDSMapping(value)}>
                            <Icon type="edit" className={styles.editIcon} />
                          </span>
                        </span>
                      }
                      className={clsx(styles.chip, styles.needsMapping)}
                      onDelete={() => onUnselect(value)}
                      onMouseDown={e => e.stopPropagation()}
                      deleteIcon={
                        <button className={styles.transparentBtn}>
                          <Icon type="cross" />
                        </button>
                      }
                      size="small"
                      variant="outlined"
                      clickable
                      aria-hidden="true"
                      tabIndex={-1}
                    />
                  </Tooltip>
                ) : isNoAccessDataset(value) ? (
                  <Tooltip
                    key={`${value}-${i}`}
                    message={intl.formatMessage({
                      id: 'reporting.no_access_to_dataset',
                      defaultMessage: 'You do not have access to this dataset'
                    })}
                  >
                    <Chip
                      label={
                        <span className={styles.sharedDataset}>
                          {getDatasetName(value)} <Icon type="info" className={styles.sharedDatasetIcon} />
                        </span>
                      }
                      className={clsx(styles.chip)}
                      onDelete={() => onUnselect(value)}
                      onMouseDown={e => e.stopPropagation()}
                      deleteIcon={
                        <button className={styles.transparentBtn}>
                          <Icon type="cross" />
                        </button>
                      }
                      size="small"
                      variant="outlined"
                      clickable
                      aria-hidden="true"
                      tabIndex={-1}
                    />
                  </Tooltip>
                ) : (
                  <Chip
                    key={`${value}-${i}`}
                    label={getDatasetName(value)}
                    className={clsx(styles.chip)}
                    onDelete={() => onUnselect(value)}
                    onMouseDown={e => e.stopPropagation()}
                    deleteIcon={
                      <button className={styles.transparentBtn}>
                        <Icon type="cross" />
                      </button>
                    }
                    size="small"
                    variant="outlined"
                    clickable
                    aria-hidden="true"
                    tabIndex={-1}
                  />
                );
              })}
            </div>
          )}
        />
      </div>

      <ConfirmModal
        isModalOpen={!!noAccessDatasetToRemove}
        onClose={() => setNoAccessDatasetToRemove(null)}
        onConfirm={onConfirmRemoveNoAccessDataset}
        title={intl.formatMessage({
          id: 'reporting.remove_no_access_dataset.title',
          defaultMessage: 'Remove dataset'
        })}
        content={intl.formatMessage({
          id: 'reporting.remove_no_access_dataset.message',
          defaultMessage:
            'You do not have access to this dataset. If you remove it, you will not be able to add it back.'
        })}
        confirmButtonText={intl.formatMessage({ id: 'common.remove', defaultMessage: 'Remove' })}
      />
    </>
  );
};
